<div class="security-settings">
    <p-toast position="top-right" key="securityToast"></p-toast> <!-- Added key for targeted messages -->

    <header class="security-settings__header">
        <h2>{{ 'settings.security.title' | translate }}</h2>
        <p>{{ 'settings.security.subtitle' | translate }}</p>
    </header>

    <p-card styleClass="security-settings__card">


        <form [formGroup]="securityForm" (ngSubmit)="onSubmit()" novalidate> <!-- Added novalidate -->
            <!-- Current Password -->
            <div class="security-settings__form-group" [class.invalid]="currentPassword.invalid && (currentPassword.dirty || currentPassword.touched)"> <!-- Check dirty as well -->
                <label for="current-password" id="current-password-label">{{ 'settings.security.currentPassword' | translate }}</label>
                <div class="p-inputgroup security-settings__form-group--password"> <!-- Use p-inputgroup for consistency -->
                    <p-password id="current-password" formControlName="currentPassword"
                                [feedback]="false" [toggleMask]="true"
                                aria-labelledby="current-password-label"
                                aria-describedby="current-password-help current-password-error"
                                aria-required="true"
                                [attr.aria-invalid]="currentPassword.invalid && (currentPassword.dirty || currentPassword.touched)"></p-password>
                </div>
                <!-- Use @if for conditional rendering -->
                @if (currentPassword.invalid && (currentPassword.dirty || currentPassword.touched)) {
                    <small id="current-password-error" class="validation-error" aria-live="assertive"> <!-- Use assertive for errors -->
                        <i class="pi pi-exclamation-triangle" aria-hidden="true"></i> <!-- Changed icon -->
                        @if (currentPassword.errors?.['required']) {
                            <span>{{ 'settings.security.required' | translate }}</span>
                        }
                    </small>
                }
                <!-- Use @if for conditional rendering -->
                @if (!currentPassword.touched) {
                    <small id="current-password-help" class="form-help-text"> <!-- Use small for help text -->
                        <i class="pi pi-info-circle" aria-hidden="true"></i>
                        <span>{{ 'settings.security.currentPasswordHelp' | translate }}</span> <!-- Added period -->
                    </small>
                }
            </div>

            <div class="security-settings__panel">
                <div formGroupName="passwordGroup">
                    <!-- New Password -->
                    <div class="security-settings__form-group" [class.invalid]="newPassword.invalid && (newPassword.dirty || newPassword.touched)">
                        <label for="new-password" id="new-password-label">{{ 'settings.security.newPassword' | translate }}</label>
                        <div class="p-inputgroup security-settings__form-group--password">
                            <p-password id="new-password" formControlName="newPassword"
                                        [feedback]="true" [toggleMask]="true"
                                        styleClass="axede-password"
                                        [weakLabel]="'settings.security.strength.weak' | translate" [mediumLabel]="'settings.security.strength.medium' | translate" [strongLabel]="'settings.security.strength.strong' | translate"
                                        aria-labelledby="new-password-label"
                                        aria-describedby="new-password-strength new-password-reqs"
                                        aria-required="true"
                                        [attr.aria-invalid]="newPassword.invalid && (newPassword.dirty || newPassword.touched)"></p-password>
                        </div>
                        <!-- Use @if for conditional rendering -->
                        @if (newPassword.value) {
                            <div id="new-password-strength" aria-live="polite" class="password-strength-indicator">
                                <span class="sr-only">{{ 'settings.security.strengthLabel' | translate }}</span>
                                <span [ngClass]="passwordStrengthClass">
                                    <i [class]="'pi ' + getStrengthIcon()" aria-hidden="true"></i>
                                    {{ passwordStrengthText }}
                                </span>
                            </div>
                        }
                    </div>

                    <!-- Confirm Password -->
                    <div class="security-settings__form-group" [class.invalid]="(confirmPassword.invalid && (confirmPassword.dirty || confirmPassword.touched)) || (showPasswordMismatch && (newPassword.dirty || confirmPassword.dirty))">
                        <label for="confirm-password" id="confirm-password-label">{{ 'settings.security.confirmPassword' | translate }}</label>
                        <div class="p-inputgroup security-settings__form-group--password">
                            <p-password id="confirm-password" formControlName="confirmPassword"
                                        [feedback]="false" [toggleMask]="true"
                                        aria-labelledby="confirm-password-label"
                                        aria-describedby="confirm-password-error"
                                        aria-required="true"
                                        [attr.aria-invalid]="(confirmPassword.invalid && (confirmPassword.dirty || confirmPassword.touched)) || showPasswordMismatch"></p-password>
                        </div>
                        <!-- Use @if for conditional rendering -->
                        @if (showPasswordMismatch && (newPassword.dirty || confirmPassword.dirty)) {
                            <small id="confirm-password-error" class="validation-error" aria-live="assertive">
                                <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
                                <span>{{ 'settings.security.passwordMismatch' | translate }}</span> <!-- Added period -->
                            </small>
                        }
                        @if (confirmPassword.errors?.['required'] && (confirmPassword.dirty || confirmPassword.touched)) {
                             <small id="confirm-password-req-error" class="validation-error" aria-live="assertive">
                                <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
                                <span>{{ 'settings.security.required' | translate }}</span>
                            </small>
                        }
                    </div>
                </div>

                <!-- Password Requirements -->
                <div class="security-settings__requirements" role="region" aria-labelledby="password-reqs-title" id="new-password-reqs">
                    <h4 class="security-settings__requirements-title" id="password-reqs-title">
                        <i class="pi pi-shield" aria-hidden="true"></i>
                        {{ 'settings.security.requirementsTitle' | translate }}
                    </h4>
                    <ul class="security-settings__requirements-list" aria-live="polite"> <!-- Announce changes -->
                        <li [class.valid]="hasMinLength"
                            [class.invalid]="showMinLengthError">
                            <i class="pi" [ngClass]="hasMinLength ? 'pi-check-circle' : (showMinLengthError ? 'pi-times-circle' : 'pi-circle')" aria-hidden="true"></i>
                            <span>{{ 'settings.security.reqMinChars' | translate:{ n: 8 } }}</span>
                        </li>
                        <li [class.valid]="hasUpperCase"
                            [class.invalid]="showUppercaseError">
                            <i class="pi" [ngClass]="hasUpperCase ? 'pi-check-circle' : (showUppercaseError ? 'pi-times-circle' : 'pi-circle')" aria-hidden="true"></i>
                            <span>{{ 'settings.security.reqUppercase' | translate }}</span>
                        </li>
                        <li [class.valid]="hasSpecialChar"
                            [class.invalid]="showSpecialCharError">
                            <i class="pi" [ngClass]="hasSpecialChar ? 'pi-check-circle' : (showSpecialCharError ? 'pi-times-circle' : 'pi-circle')" aria-hidden="true"></i>
                            <span>{{ 'settings.security.reqSpecialChar' | translate }}</span> <!-- Simplified example -->
                        </li>
                        <li [class.valid]="!containsUsername"
                            [class.invalid]="showUsernameError">
                            <i class="pi" [ngClass]="!containsUsername && newPassword.value ? 'pi-check-circle' : (showUsernameError ? 'pi-times-circle' : 'pi-circle')" aria-hidden="true"></i>
                            <span>{{ 'settings.security.reqNoUsername' | translate }}</span>
                        </li>
                        <li class="requirement-info"> <!-- Added class for styling -->
                            <i class="pi pi-info-circle" aria-hidden="true"></i>
                            <span>{{ 'settings.security.reqNotEasy' | translate }}</span> <!-- Simplified -->
                        </li>
                    </ul>
                    <!-- Use @if/@else for conditional rendering -->
                    @if (showPasswordTips) {
                        <div class="password-tips axede-fade-in"> <!-- Added animation class -->
                            <h5>{{ 'settings.security.tipsTitle' | translate }}</h5> <!-- Simplified title -->
                            <ul>
                                <li>{{ 'settings.security.tip1' | translate }}</li>
                                <li>{{ 'settings.security.tip2' | translate }}</li>
                                <li>{{ 'settings.security.tip3' | translate }}</li> <!-- Simplified -->
                            </ul>
                            <button type="button" class="p-button p-button-text p-button-sm toggle-tips-button" (click)="togglePasswordTips()"> <!-- Use p-button for consistency -->
                                <i class="pi pi-times-circle" aria-hidden="true"></i> {{ 'settings.security.hideTips' | translate }}
                            </button>
                        </div>
                    } @else {
                        <button type="button" class="p-button p-button-text p-button-sm show-tips-link" (click)="togglePasswordTips()"> <!-- Use p-button for consistency -->
                            <i class="pi pi-info-circle" aria-hidden="true"></i> {{ 'settings.security.showTips' | translate }}
                        </button>
                    }
                </div>
            </div>

            <footer class="security-settings__actions">
                <button pButton type="button" [label]="'settings.security.cancel' | translate"
                      class="p-button-secondary p-button-rounded axede-button-secondary"
                      (click)="cancelForm()"
                      icon="pi pi-times"></button>
                <button pButton type="submit" [label]="'settings.security.save' | translate"
                      class="p-button-success p-button-rounded axede-button-primary"
                      icon="pi pi-save"
                      [loading]="submitting"
                      loadingIcon="pi pi-spinner pi-spin"
                      [disabled]="securityForm.invalid || submitting || !formChanged"
                      aria-disabled="securityForm.invalid || submitting || !formChanged"></button>
            </footer>

            @if (lastUpdated) {
                <div class="form-status" aria-live="polite">
                    <i class="pi pi-clock" aria-hidden="true"></i>
                    <span>{{ 'settings.security.lastUpdated' | translate }} {{lastUpdated | date:'medium'}}</span>
                </div>
            }
        </form>
    </p-card>
</div>
